import { Component, OnDestroy, OnInit } from '@angular/core';
import {
  AdminContactDepartment,
  AdminContactInquiry,
  CONTACT_INQUIRY_STATUS_CLASSES,
  CONTACT_INQUIRY_STATUS_LABELS,
  ContactInquiryStatus
} from '@app/model/contact.model';
import { ContactManagementDataService } from '@app/services/contact-management-data.service';
import { Subject, Subscription } from 'rxjs';
import { debounceTime, distinctUntilChanged, finalize, take, takeUntil } from 'rxjs/operators';
import { MatDialog } from '@angular/material';
import { ContactInquiryDetailComponent } from '../contact-inquiry-detail/contact-inquiry-detail.component';
import { LayoutUtilsService } from '@app/core/_base/crud';
import { ToastrService } from 'ngx-toastr';

@Component({
  selector: 'app-contact-inquiry-management',
  templateUrl: './contact-inquiry-management.component.html',
  styleUrls: ['./contact-inquiry-management.component.scss']
})
export class ContactInquiryManagementComponent implements OnInit, OnDestroy {
  inquiries: AdminContactInquiry[] = [];
  departments: AdminContactDepartment[] = [];

  searchText = '';
  statusFilter: 'all' | ContactInquiryStatus = 'all';
  departmentFilter = 'all';

  pageIndex = 1;
  pageSize = 10;
  totalItems = 0;
  pageSizeOptions: number[] = [5, 10, 20, 100];

  isLoading = false;
  errorMessage = '';
  deletingInquiryId = '';

  readonly statusLabels = CONTACT_INQUIRY_STATUS_LABELS;
  readonly statusClasses = CONTACT_INQUIRY_STATUS_CLASSES;
  readonly statusOptions: { value: ContactInquiryStatus; label: string }[] = [
    { value: 'new', label: CONTACT_INQUIRY_STATUS_LABELS.new },
    { value: 'in_progress', label: CONTACT_INQUIRY_STATUS_LABELS.in_progress },
    { value: 'resolved', label: CONTACT_INQUIRY_STATUS_LABELS.resolved },
    { value: 'closed', label: CONTACT_INQUIRY_STATUS_LABELS.closed }
  ];

  private destroy$ = new Subject<void>();
  private searchSubject = new Subject<string>();
  private inquiriesSubscription = new Subscription();

  constructor(
    private contactManagementDataService: ContactManagementDataService,
    private dialog: MatDialog,
    private layoutUtilsService: LayoutUtilsService,
    private toastr: ToastrService
  ) {}

  ngOnInit(): void {
    this.loadDepartments();

    // Debounce search 500ms then reload from the server.
    this.searchSubject
      .pipe(debounceTime(500), distinctUntilChanged(), takeUntil(this.destroy$))
      .subscribe(() => {
        this.pageIndex = 1;
        this.loadInquiries();
      });

    this.loadInquiries();
  }

  ngOnDestroy(): void {
    this.inquiriesSubscription.unsubscribe();
    this.destroy$.next();
    this.destroy$.complete();
    this.searchSubject.complete();
  }

  getStatusLabel(status: ContactInquiryStatus): string {
    return this.statusLabels[status] || status;
  }

  getStatusClass(status: ContactInquiryStatus): string {
    return this.statusClasses[status] || '';
  }

  formatDate(value: string | null | undefined): string {
    if (!value) {
      return '—';
    }
    const date = new Date(value);
    if (isNaN(date.getTime())) {
      return value;
    }
    const dd = String(date.getDate()).padStart(2, '0');
    const mm = String(date.getMonth() + 1).padStart(2, '0');
    const yyyy = date.getFullYear();
    const hh = String(date.getHours()).padStart(2, '0');
    const mi = String(date.getMinutes()).padStart(2, '0');
    return dd + '/' + mm + '/' + yyyy + ' ' + hh + ':' + mi;
  }

  loadDepartments(): void {
    this.contactManagementDataService
      .getDepartments({ sorts: 'sort_order', pageSize: 1000 })
      .pipe(takeUntil(this.destroy$))
      .subscribe(
        (departments) => {
          this.departments = departments || [];
        },
        (error) => {
          console.error('Load departments for filter failed:', error);
        }
      );
  }

  /** Build the Sieve filters string from search/status/department (AND via comma). */
  private buildFilters(): string {
    const conditions: string[] = [];
    const keyword = (this.searchText || '').trim();
    if (keyword) {
      // Per guide: name|email|phone|company|subject|message@=keyword
      conditions.push(`name|email|phone|company|subject|message@=${keyword}`);
    }
    if (this.statusFilter !== 'all') {
      conditions.push(`status==${this.statusFilter}`);
    }
    if (this.departmentFilter !== 'all') {
      conditions.push(`departmentId==${this.departmentFilter}`);
    }
    return conditions.join(',');
  }

  loadInquiries(showLoading: boolean = true): void {
    if (showLoading) {
      this.isLoading = true;
    }
    this.errorMessage = '';

    this.inquiriesSubscription.unsubscribe();
    this.inquiriesSubscription = this.contactManagementDataService
      .getInquiriesPaged({
        filters: this.buildFilters(),
        sorts: '-created_at',
        page: this.pageIndex,
        pageSize: this.pageSize
      })
      .pipe(takeUntil(this.destroy$))
      .subscribe(
        (result) => {
          this.inquiries = (result && result.collection) || [];
          this.totalItems = (result && result.total) || 0;
          this.isLoading = false;
        },
        (error) => {
          console.error('Load contact inquiries failed:', error);
          if (showLoading) {
            this.inquiries = [];
            this.totalItems = 0;
          }
          this.isLoading = false;
          this.errorMessage = 'Không thể tải danh sách thư liên hệ';
        }
      );
  }

  onSearchChange(value: string): void {
    this.searchText = value;
    this.searchSubject.next(value);
  }

  onStatusFilterChange(): void {
    this.pageIndex = 1;
    this.loadInquiries();
  }

  onDepartmentFilterChange(): void {
    this.pageIndex = 1;
    this.loadInquiries();
  }

  clearFilters(): void {
    this.searchText = '';
    this.statusFilter = 'all';
    this.departmentFilter = 'all';
    this.pageIndex = 1;
    this.loadInquiries();
  }

  onPageChange(pageNumber: number): void {
    if (typeof pageNumber !== 'number') {
      return;
    }
    this.pageIndex = pageNumber;
    this.loadInquiries();
    window.scrollTo(0, 0);
  }

  onPageSizeChange(newSize: number): void {
    this.pageSize = newSize;
    this.pageIndex = 1;
    this.loadInquiries();
  }

  openInquiryDetail(inquiry: AdminContactInquiry): void {
    const dialogRef = this.dialog.open(ContactInquiryDetailComponent, {
      width: '760px',
      maxWidth: 'calc(100vw - 32px)',
      maxHeight: '90vh',
      autoFocus: false,
      disableClose: true,
      panelClass: 'contact-inquiry-detail-dialog',
      data: { inquiry }
    });

    dialogRef
      .afterClosed()
      .pipe(takeUntil(this.destroy$))
      .subscribe((result) => {
        if (!result) {
          return;
        }

        if (result.updated) {
          // Update the local item then reload the current page to keep total in sync with the server.
          this.inquiries = this.inquiries.map((item) =>
            item.id === inquiry.id
              ? {
                  ...item,
                  status: result.status,
                  admin_notes:
                    result.adminNotes !== undefined ? result.adminNotes : item.admin_notes
                }
              : item
          );
          this.loadInquiries(false);
        } else if (result.deleted) {
          this.loadInquiries(false);
        }
      });
  }

  confirmDeleteInquiry(inquiry: AdminContactInquiry): void {
    const title = 'Xóa thư liên hệ';
    const description =
      'Bạn có chắc chắn muốn xóa thư "' + (inquiry.subject || '') + '" từ "' + (inquiry.full_name || '') + '"?';
    const waitDescription = 'Đang xóa thư liên hệ...';

    const dialogRef = this.layoutUtilsService.deleteElement(title, description, waitDescription);

    dialogRef
      .afterClosed()
      .pipe(take(1))
      .subscribe((confirmed) => {
        if (!confirmed) {
          return;
        }
        this.deleteInquiry(inquiry);
      });
  }

  private deleteInquiry(inquiry: AdminContactInquiry): void {
    this.deletingInquiryId = inquiry.id;

    this.contactManagementDataService
      .deleteInquiry(inquiry.id)
      .pipe(
        take(1),
        finalize(() => {
          this.deletingInquiryId = '';
        })
      )
      .subscribe(
        (response) => {
          if (!response || !response.success) {
            this.toastr.error(response && response.message ? response.message : 'Không thể xóa thư liên hệ');
            return;
          }
          this.toastr.success('Xóa thư liên hệ thành công');
          this.loadInquiries(false);
        },
        (error) => {
          const errorResponse = error && error.error ? error.error : null;
          let message = 'Không thể xóa thư liên hệ';
          if (errorResponse && errorResponse.message) {
            message = errorResponse.message;
          } else if (errorResponse && errorResponse.title) {
            message = errorResponse.title;
          }
          this.toastr.error(message);
        }
      );
  }
}
